import { apiClient } from "@/lib/api-client";
import type {
  GetEventListResp,
  GetMessageListParams,
  GetMessageListResp,
} from "@/types/threadEvents.ts";
import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";

/**
 * Canonical thread-feed definition for both web and native via the `@/shared`
 * symlink. The only per-app dependency is `@/lib/api-client`, which resolves
 * against each app's own src directory.
 *
 * Web uses `apiClient` here because shared code cannot import its typed `api`
 * wrapper. The adapter re-exports the same configured axios instance, preserving
 * the single 401 interceptor; ordinary web code still uses `api`.
 */

/** Which feed of a thread a caller means: the conversation, or the admin event timeline. */
export type ThreadFeedView = "messages" | "events";

/**
 * Initial page size for the thread message feed, shared by every surface that
 * renders one. It is a single value on purpose: page size lives inside the
 * query key, so a surface that picks its own silently gets a second cache entry
 * for the same thread and fetches it separately.
 *
 * 30 is a floor, not a free parameter — `useOmniReview`'s turn-boundary
 * walk needs a window wide enough to find the turn's user message.
 */
export const THREAD_MESSAGE_PAGE_SIZE = 30;

/** Initial page size for the admin event timeline, which is far denser than the message feed. */
export const THREAD_EVENT_PAGE_SIZE = 100;

/**
 * Options every feed query shares.
 *
 * `staleTime: 0` keeps observer mounts canonical. Explicit repair triggers use
 * `invalidateQueries`, which refetches active observers regardless of
 * staleness.
 *
 * `refetchOnWindowFocus` repairs the web feed when its tab regains focus. Native
 * does not wire react-query's `focusManager` to `AppState`; its foreground
 * invalidation supplies the equivalent repair.
 *
 * `refetchOnReconnect` stays off because an unprompted refetch can disturb the
 * user's scroll position. Explicit reconnect repair runs only when a gap is
 * known to have occurred.
 */
const sharedFeedOptions = {
  staleTime: 0,
  refetchOnMount: true,
  refetchOnWindowFocus: true,
  refetchOnReconnect: false,
} as const;

/**
 * Query definitions for a thread's message and event feeds.
 *
 * Keys nest so that `thread(threadId)` is a prefix of both feeds and every page
 * size, which is what lets a repair trigger heal the whole thread with one
 * invalidation. `byIds` deliberately sits outside that prefix — see its docs.
 */
export const messageQueries = {
  /** Every thread's feeds. Only a whole-app repair (native's foregrounding) should reach this wide. */
  all: () => ["messages"] as const,

  /** One thread's feeds — both views, every page size. The prefix repair triggers invalidate. */
  thread: (threadId: number) => [...messageQueries.all(), threadId] as const,

  /**
   * The prefix for one view of one thread, for callers that want to refresh
   * only what is on screen. Note the messages view's prefix is the whole
   * thread, so invalidating it also reaches a cached event list.
   */
  feed: (threadId: number, view: ThreadFeedView) =>
    view === "events"
      ? ([...messageQueries.thread(threadId), "events"] as const)
      : messageQueries.thread(threadId),

  /** The conversation feed — user and assistant messages, newest page first. */
  list: (threadId: number, limit: number = THREAD_MESSAGE_PAGE_SIZE) =>
    infiniteQueryOptions({
      ...sharedFeedOptions,
      queryKey: [...messageQueries.thread(threadId), { limit }] as const,
      queryFn: async ({ pageParam, queryKey, signal }) => {
        const [, keyThreadId, options] = queryKey;
        const params: GetMessageListParams = { ...options, cursor: pageParam };

        const response = await apiClient.get<GetMessageListResp>(
          `/api/a/threads/${keyThreadId}/messages/list`,
          { params, signal },
        );

        return response.data;
      },
      initialPageParam: undefined as string | undefined,
      // Pages grow at the front via fetchPreviousPage, so a full refetch walks
      // each page forward through its own cursor. An empty terminal page has no
      // cursor and must stop the walk instead of reusing an earlier page's.
      getNextPageParam: (lastPage) => lastPage.nextCursor,
      getPreviousPageParam: (firstPage) => firstPage?.prevCursor,
      // Don't seed `initialData`: an empty seed makes the query render as
      // successful while its first request is still in flight. The feed must
      // start pending so consumers can show their loading state.
    }),

  /**
   * The full thread EVENT list — every persisted event type, not just messages
   * — for the admin debug timeline. Admin-gated server-side. Nested under
   * `thread(threadId)` so a thread-wide repair covers it too.
   */
  eventList: (threadId: number, limit: number = THREAD_EVENT_PAGE_SIZE) =>
    infiniteQueryOptions({
      ...sharedFeedOptions,
      queryKey: [...messageQueries.feed(threadId, "events"), { limit }] as const,
      queryFn: async ({ pageParam, queryKey, signal }) => {
        const [, keyThreadId, , options] = queryKey;
        const params: GetMessageListParams = { ...options, cursor: pageParam };

        const response = await apiClient.get<GetEventListResp>(
          `/api/a/threads/${keyThreadId}/messages/event-list`,
          { params, signal },
        );

        return response.data;
      },
      initialPageParam: undefined as string | undefined,
      getNextPageParam: (lastPage) => lastPage.nextCursor,
      getPreviousPageParam: (firstPage) => firstPage?.prevCursor,
    }),

  /**
   * Fetch specific events by id, for reconciling the handful of rows a socket
   * update says changed instead of refetching every loaded page.
   *
   * Keyed under the `["messages"]` root but deliberately OUTSIDE
   * `thread(threadId)`: the send path cancels the thread prefix on every
   * message, and a targeted fetch inside that blast radius would be aborted by
   * an unrelated list invalidation.
   */
  byIds: (threadId: number, eventIds: number[], view: ThreadFeedView) =>
    queryOptions({
      queryKey: [...messageQueries.all(), "byIds", threadId, { eventIds, view }] as const,
      queryFn: async ({ signal }) => {
        const response = await apiClient.get<GetMessageListResp | GetEventListResp>(
          `/api/a/threads/${threadId}/messages/by-ids`,
          { params: { eventIds: eventIds.join(","), view }, signal },
        );
        return response.data;
      },
      enabled: eventIds.length > 0,
      staleTime: 0,
    }),
};
